import { useState, useEffect, Fragment } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import DataTable from 'react-data-table-component';
import { Badge } from 'react-bootstrap';

import { sortTableDateFecha } from '../../utils/generalFunctions';

import { editarBreadcrumb } from '../../actions/breadcrumb';
import { buscarSolicitudesWA } from '../../actions/solicitudes_wa';

import DetallesSolicitudWA from './DetallesSolicitudWA';
import EliminarSolicitudWA from './EliminarSolicitudWA';

const SolicitudesWA = () => {

    let rutasBread = [
        {
            activo: true,
            nombre: 'Solicitudes WhatsApp',
            path: '/solicitudes_wa'
        }
    ];

    const dispatch = useDispatch();

    const { usuario } = useSelector(state => state.auth);
    const { listado } = useSelector(state => state.solicitudes_wa);

    const [solicitudes, setSolicitudes] = useState([]);
    const [tabActivo, setTabActivo] = useState(0);

    const [detallesSolicitud, setDetallesSolicitud] = useState(null);
    const [eliminarSolicitud, setEliminarSolicitud] = useState(null);

    const paginationOptions = { rowsPerPageText: 'Filas por página', rangeSeparatorText: 'de' };

    const columnas = [
        {
            name: 'Fecha',
            sortable: true,
            sortFunction: sortTableDateFecha,
            cell: item => item.fecha
                ? new Date(item.fecha).toLocaleDateString('es-MX', { year: 'numeric', month: 'long', day: 'numeric' })
                : 'Sin definir',
            grow: 1.5
        },
        {
            name: 'Número',
            sortable: true,
            selector: row => row.wa_id || '—',
            cell: row => row.wa_id
                ? <span>{row.wa_id} <a href={`https://wa.me/${row.wa_id.replace(/\D/g, '')}`} target="_blank" rel="noreferrer"><i className="fab fa-whatsapp text-success"></i></a></span>
                : '—',
            grow: 1
        },
        {
            name: 'Contacto',
            sortable: true,
            selector: row => row.nombre_wa || '—',
            grow: 1.5
        },
        {
            name: 'Cliente',
            sortable: true,
            selector: row => row.cliente
                ? [row.cliente.nombre, row.cliente.apellido_paterno, row.cliente.apellido_materno].filter(Boolean).join(' ')
                : null,
            cell: row => row.cliente
                ? <span>{[row.cliente.nombre, row.cliente.apellido_paterno, row.cliente.apellido_materno].filter(Boolean).join(' ')}</span>
                : <h6 className="mb-0"><Badge pill bg="secondary">Desconocido</Badge></h6>,
            grow: 2
        },
        {
            name: 'Asesor asignado',
            sortable: true,
            selector: row => row.usuario
                ? [row.usuario.nombre, row.usuario.apellido_paterno].filter(Boolean).join(' ')
                : null,
            cell: row => row.usuario
                ? <span>{[row.usuario.nombre, row.usuario.apellido_paterno].filter(Boolean).join(' ')}</span>
                : <h6 className="mb-0"><Badge pill bg="warning">Sin asignar</Badge></h6>,
            grow: 2
        },
        {
            name: 'Estado',
            sortable: true,
            selector: row => row.status,
            cell: row => row.status === 0
                ? <h6 className="mb-0"><Badge pill bg="warning">Pendiente</Badge></h6>
                : row.status === 2
                    ? <h6 className="mb-0"><Badge pill bg="danger">Rechazada</Badge></h6>
                    : <h6 className="mb-0"><Badge pill bg="success">Atendida</Badge></h6>,
            grow: 1
        },
        {
            name: '',
            grow: 0.5,
            cell: row => (
                <div className="text-right w-100">
                    {
                        usuario?.permisos?.solicitudes?.eliminar &&
                            <button
                                type="button"
                                className="btn btn-sm btn-link px-2 float-end"
                                onClick={() => setEliminarSolicitud(row)}
                            >
                                <i className="fas fa-times text-danger"></i>
                            </button>
                    }
                    {
                        usuario?.permisos?.solicitudes?.detalles &&
                            <button
                                type="button"
                                className="btn btn-sm btn-link px-2 float-end"
                                onClick={() => setDetallesSolicitud(row)}
                            >
                                <i className="fas fa-eye text-info"></i>
                            </button>
                    }
                </div>
            )
        }
    ];

    const handleTabChange = (status) => {
        setTabActivo(status);
        dispatch(buscarSolicitudesWA({ status }));
    }

    useEffect(() => {
        dispatch(buscarSolicitudesWA({ status: 0 }));
        dispatch(editarBreadcrumb(rutasBread));
    }, []);

    useEffect(() => {
        setSolicitudes(listado);
    }, [listado]);

    return (
        <Fragment>
            <DetallesSolicitudWA
                solicitud={detallesSolicitud}
                reestablecerSolicitud={setDetallesSolicitud}
            />
            <EliminarSolicitudWA
                solicitud={eliminarSolicitud}
                reestablecerSolicitud={setEliminarSolicitud}
            />

            <div className="row">
                <div className="col-md-10 offset-md-1 pb-4">
                    <h3 className="my-5">Solicitudes WhatsApp</h3>

                    {/* Tabs de filtro */}
                    <div className="btn-group mb-4" role="group">
                        <button
                            type="button"
                            className={`btn ${tabActivo === 0 ? 'btn-warning' : 'btn-outline-warning'}`}
                            onClick={() => handleTabChange(0)}
                        >
                            Pendientes
                        </button>
                        <button
                            type="button"
                            className={`btn ${tabActivo === 1 ? 'btn-success' : 'btn-outline-success'}`}
                            onClick={() => handleTabChange(1)}
                        >
                            Atendidas
                        </button>
                        <button
                            type="button"
                            className={`btn ${tabActivo === 2 ? 'btn-danger' : 'btn-outline-danger'}`}
                            onClick={() => handleTabChange(2)}
                        >
                            Rechazadas
                        </button>
                        <button
                            type="button"
                            className={`btn ${tabActivo === -1 ? 'btn-primary' : 'btn-outline-primary'}`}
                            onClick={() => handleTabChange(-1)}
                        >
                            Todas
                        </button>
                    </div>

                    <DataTable
                        columns={columnas}
                        data={solicitudes}
                        noDataComponent="No hay solicitudes registradas"
                        pagination
                        paginationPerPage={20}
                        paginationRowsPerPageOptions={[20, 50, 100]}
                        paginationComponentOptions={paginationOptions}
                        highlightOnHover={true}
                    />
                </div>
            </div>
        </Fragment>
    )
}

export default SolicitudesWA;
